import React, { useState, useEffect, useMemo } from "react";
import { getFirestore, collection, getDocs, query, where, orderBy, doc, getDoc } from "firebase/firestore";
import { Table, TableBody, TableContainer, TableHead, Paper, TextField, Box, TableSortLabel, Button, Tooltip, CircularProgress, Typography } from "@mui/material";
import { StyledTableCell, StyledTableRow } from '../../utils/styled';
import { getPricesOnce } from "../../services/priceService";
import { getCuadrillasOnce } from "../../services/cuadrillasService";
import moment from "moment";
import { utils, writeFile } from "xlsx";
import "moment/locale/es";

const firestore = getFirestore();
const currentUser = JSON.parse(localStorage.getItem("currentUser"));

const pricesById = (prices) => {
    if (!prices) return {};
    return Array.isArray(prices) ? prices.reduce((acc, p) => { acc[p.id] = p; return acc; }, {}) : prices;
};

const priceValueServices = (services, tipoDia, pricesMap) => {
    if (!services || !Array.isArray(services)) return 0;
    return services.reduce((total, service) => {
        if (!pricesMap || !pricesMap[service.id]) return total;
        const typeValue = typeof service.value === 'boolean';
        const priceHogar = tipoDia === 'Semana'
            ? parseInt(pricesMap[service.id]?.hogar, 10) || 0
            : parseInt(pricesMap[service.id]?.feriado_domingo_hogar, 10) || 0;
        return total + (typeValue ? priceHogar : priceHogar * (parseInt(service.value, 10) || 0));
    }, 0);
};

const calculateGarantiaTotal = (garantia, pricesMap) => {
    const data = garantia[0] || garantia;
    return priceValueServices(data?.services, data?.tipoDia, pricesMap);
};

const getCuadrillaPercentage = (cuadrillaName, cuadrillasList) => {
    if (!cuadrillaName || !cuadrillasList || cuadrillasList.length === 0) return 100;
    const nameUpper = String(cuadrillaName).toUpperCase().trim();
    const firstToken = nameUpper.split(' ')[0];

    const match = cuadrillasList.find(c => {
        const cNum = (c.numero || '').toUpperCase().trim();
        const cUser = (c.usuario || '').toUpperCase().trim();
        const cCod = (c.codigo || '').toUpperCase().trim();

        if (cNum && (cNum === nameUpper || cNum.split(' ')[0] === firstToken)) return true;
        if (cCod && (cCod === nameUpper || cCod === firstToken)) return true;
        if (cUser && (cUser === nameUpper || nameUpper.includes(cUser))) return true;

        return false;
    });

    if (!match || match.porcentajeParticipacion === undefined || match.porcentajeParticipacion === null || match.porcentajeParticipacion === '') {
        return 100;
    }
    const pct = parseFloat(match.porcentajeParticipacion);
    return isNaN(pct) ? 100 : pct;
};

const formatCurrency = (amount) => {
    if (!amount) return '₲ 0';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '₲ 0';
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n);
};

const EstadisticasGarantias = () => {
    const [mesSeleccionado, setMesSeleccionado] = useState(moment().format("YYYY-MM"));
    const [garantias, setGarantias] = useState(null);
    const [prices, setPrices] = useState([]);
    const [cuadrillas, setCuadrillas] = useState([]);
    const [sortBy, setSortBy] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc');
    const [feriados, setFeriados] = useState({});

    const isAdmin = currentUser?.rol === 'admin';

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc');
        }
    };

    useEffect(() => {
        getPricesOnce(setPrices);
        getCuadrillasOnce(setCuadrillas, { soloActivas: false });
    }, []);

    useEffect(() => {
        const cargarGarantias = async () => {
            setGarantias(null);
            try {
                const [year, month] = mesSeleccionado.split("-");
                const inicioMes = `${year}${month}01`;
                const finMes = `${year}${month}31`;

                // Cargar feriados del mes
                const diasDocRef = doc(firestore, `asistencias/${year}/${month}/dias`);
                const diasDocSnap = await getDoc(diasDocRef);
                if (diasDocSnap.exists()) {
                    setFeriados(diasDocSnap.data().feriados || {});
                } else {
                    setFeriados({});
                }

                const garantiasRef = collection(firestore, "garantias");
                const q = query(
                    garantiasRef,
                    where("date", ">=", inicioMes),
                    where("date", "<=", finMes),
                    orderBy("date")
                );

                const snapshot = await getDocs(q);
                setGarantias(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) {
                console.error("Error loading warranties statistics:", err);
                setGarantias([]);
            }
        };

        cargarGarantias();
    }, [mesSeleccionado]);

    const garantiasPorCuadrilla = useMemo(() => {
        const pricesMap = pricesById(prices);
        const agrupadas = {};
        if (!garantias) return agrupadas;

        garantias.forEach(doc => {
            const data = doc[0] || doc;
            const cuadrilla = data?.otRelacionada?.cuadrilla || data?.cuadrilla;
            const date = data?.date;
            if (!cuadrilla || !date) return;

            const dia = parseInt(date.slice(-2));
            const key = cuadrilla;

            if (!agrupadas[key]) {
                agrupadas[key] = { cuadrilla, total: 0, montoTotal: 0, montoCalculado: 0, dias: {} };
            }
            if (!agrupadas[key].dias[dia]) {
                agrupadas[key].dias[dia] = 0;
            }

            agrupadas[key].dias[dia]++;
            agrupadas[key].total++;
            const baseMonto = calculateGarantiaTotal(doc, pricesMap);
            const pct = getCuadrillaPercentage(cuadrilla, cuadrillas);
            const montoCalc = baseMonto * (pct / 100);

            agrupadas[key].montoTotal += baseMonto;
            agrupadas[key].montoCalculado += montoCalc;
        });

        return Object.keys(agrupadas).sort().reduce((acc, key) => {
            acc[key] = agrupadas[key];
            return acc;
        }, {});
    }, [garantias, prices, cuadrillas]);

    const diasMes = moment(mesSeleccionado, "YYYY-MM").daysInMonth();

    const sortedKeys = useMemo(() => {
        const keys = Object.keys(garantiasPorCuadrilla);
        if (!sortBy) return keys.sort();
        return [...keys].sort((a, b) => {
            const rowA = garantiasPorCuadrilla[a];
            const rowB = garantiasPorCuadrilla[b];
            let valA, valB;
            if (sortBy === 'total') {
                valA = rowA.total ?? 0;
                valB = rowB.total ?? 0;
            } else if (sortBy === 'montoCalculado') {
                valA = rowA.montoCalculado ?? 0;
                valB = rowB.montoCalculado ?? 0;
            } else if (sortBy === 'montoTotal') {
                valA = rowA.montoTotal ?? 0;
                valB = rowB.montoTotal ?? 0;
            }
            if (valA === valB) return a.localeCompare(b);
            const mult = sortOrder === 'asc' ? 1 : -1;
            return mult * (valA - valB);
        });
    }, [garantiasPorCuadrilla, sortBy, sortOrder]);

    const handleDownloadExcel = () => {
        if (sortedKeys.length === 0) return;

        const headers = [
            "Cuadrilla",
            ...Array.from({ length: diasMes }, (_, i) => String(i + 1)),
            "Total",
            "Monto (% Part.)",
            ...(isAdmin ? ["Monto Total"] : [])
        ];
        
        const rows = sortedKeys.map(key => {
            const row = garantiasPorCuadrilla[key];
            const dataRow = {
                "Cuadrilla": row.cuadrilla
            };
            for (let i = 1; i <= diasMes; i++) {
                dataRow[String(i)] = row.dias[i] || 0;
            }
            dataRow["Total"] = row.total;
            dataRow["Monto (% Part.)"] = row.montoCalculado;
            if (isAdmin) {
                dataRow["Monto Total"] = row.montoTotal;
            }
            return dataRow;
        });

        const ws = utils.json_to_sheet(rows, { header: headers });
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Garantías por Cuadrilla");
        
        ws['!cols'] = [
            { wch: 15 }, // Cuadrilla
            ...Array.from({ length: diasMes }, () => ({ wch: 5 })), // Days
            { wch: 8 },  // Total
            { wch: 15 }, // Monto (% Part.)
            ...(isAdmin ? [{ wch: 15 }] : [])  // Monto Total
        ];

        writeFile(wb, `garantias_por_cuadrilla_${mesSeleccionado}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`);
    };

    const sortLabelSx = {
        color: 'white !important',
        '& .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
        '&:hover': { color: 'rgba(255,255,255,0.85) !important' },
        '&:hover .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.85) !important', opacity: 1 },
        '&.Mui-active': { color: 'white !important' },
        '&.Mui-active .MuiTableSortLabel-icon': { color: 'white !important', opacity: 1 },
    };

    return (
        <div>
            <h2>Estadísticas de garantías por cuadrilla</h2>
            <Box display="flex" justifyContent="center" alignItems="center" gap={2} marginBottom={2}>
                <TextField
                    type="month"
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(e.target.value)}
                />
                <Button
                    variant="contained"
                    color="success"
                    onClick={handleDownloadExcel}
                    disabled={sortedKeys.length === 0}
                >
                    Descargar Excel
                </Button>
            </Box>
            <TableContainer
                component={Paper}
                sx={{
                    maxHeight: '75vh',
                    overflow: 'auto',
                    '& .MuiTableCell-root': {
                        borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                        padding: '2px 4px',
                    },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none'
                    }
                }}
            >
                <Table size="small">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell
                                sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1001,
                                    minWidth: '180px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Cuadrilla
                            </StyledTableCell>
                            {[...Array(diasMes).keys()].map(i => {
                                const dayNum = i + 1;
                                const fecha = moment(mesSeleccionado, "YYYY-MM").date(dayNum).locale('es');
                                const diaSemana = fecha.format('dd');
                                const diaCapitalizado = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
                                const esDomingo = fecha.day() === 0;
                                const esFinde = esDomingo || fecha.day() === 6;
                                const esFeriado = !!feriados[dayNum];
                                const headerSx = esFeriado
                                    ? { backgroundColor: '#c9b501 !important', color: '#827717' }
                                    : esDomingo
                                        ? { backgroundColor: '#f6882d !important', color: '#e65100' }
                                        : {};
                                return (
                                    <Tooltip key={i} title={esFeriado ? `Feriado: ${feriados[dayNum]}` : ''} arrow disableHoverListener={!esFeriado}>
                                        <StyledTableCell
                                            align="center"
                                            sx={{
                                                minWidth: '36px',
                                                width: '36px',
                                                padding: '2px 4px',
                                                lineHeight: 1,
                                                ...headerSx,
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                                                <span style={{ fontSize: '0.6rem', opacity: 0.75, fontWeight: esFinde || esFeriado ? 700 : 400 }}>
                                                    {diaCapitalizado}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {dayNum}
                                                </span>
                                            </div>
                                        </StyledTableCell>
                                    </Tooltip>
                                );
                            })}
                            <StyledTableCell align="center" sortDirection={sortBy === 'total' ? sortOrder : false} sx={{ whiteSpace: 'nowrap' }}>
                                <TableSortLabel
                                    active={sortBy === 'total'}
                                    direction={sortBy === 'total' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('total')}
                                    sx={sortLabelSx}
                                >
                                    Total
                                </TableSortLabel>
                            </StyledTableCell>
                            <StyledTableCell align="center" sortDirection={sortBy === 'montoCalculado' ? sortOrder : false} sx={{ whiteSpace: 'nowrap' }}>
                                <TableSortLabel
                                    active={sortBy === 'montoCalculado'}
                                    direction={sortBy === 'montoCalculado' ? sortOrder : 'desc'}
                                    onClick={() => handleSort('montoCalculado')}
                                    sx={sortLabelSx}
                                >
                                    Monto (% part.)
                                </TableSortLabel>
                            </StyledTableCell>
                            {isAdmin && (
                                <StyledTableCell align="center" sortDirection={sortBy === 'montoTotal' ? sortOrder : false} sx={{ whiteSpace: 'nowrap' }}>
                                    <TableSortLabel
                                        active={sortBy === 'montoTotal'}
                                        direction={sortBy === 'montoTotal' ? sortOrder : 'desc'}
                                        onClick={() => handleSort('montoTotal')}
                                        sx={sortLabelSx}
                                    >
                                        Monto total
                                    </TableSortLabel>
                                </StyledTableCell>
                            )}
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {sortedKeys?.length > 0 ? (
                            sortedKeys.map((key) => {
                                const row = garantiasPorCuadrilla[key];
                                return (
                                    <StyledTableRow key={key} sx={{ height: '32px' }}>
                                        <StyledTableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1000,
                                                minWidth: '180px',
                                                backgroundColor: '#ffffff',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.7rem',
                                            }}
                                        >
                                            {row.cuadrilla}
                                        </StyledTableCell>
                                        {[...Array(diasMes).keys()].map(i => {
                                            const dayNum = i + 1;
                                            const count = row.dias[dayNum] || 0;
                                            const hasOrders = count > 0;

                                            const fecha = moment(mesSeleccionado, "YYYY-MM").date(dayNum);
                                            const esDomingo = fecha.day() === 0;
                                            const esFeriado = !!feriados[dayNum];

                                            let cellBg = 'inherit';
                                            if (esDomingo || esFeriado) {
                                                cellBg = hasOrders ? "#c8e6c9" : esDomingo ? "#ffcc80" : "#fff176";
                                            } else {
                                                cellBg = hasOrders ? 'inherit' : '#e8f5e9';
                                            }

                                            return (
                                                <StyledTableCell
                                                    key={dayNum}
                                                    align="center"
                                                    sx={{
                                                        backgroundColor: cellBg,
                                                        minWidth: '36px',
                                                        width: '36px',
                                                        padding: '2px 4px',
                                                        fontSize: '0.8rem',
                                                    }}
                                                >
                                                    {count || "-"}
                                                </StyledTableCell>
                                            );
                                        })}
                                        <StyledTableCell align="center">{row.total}</StyledTableCell>
                                        <StyledTableCell align="center">{formatCurrency(row.montoCalculado)}</StyledTableCell>
                                        {isAdmin && (
                                            <StyledTableCell align="center">{formatCurrency(row.montoTotal)}</StyledTableCell>
                                        )}
                                    </StyledTableRow>
                                );
                            })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={diasMes + (isAdmin ? 3 : 2)} align="center">
                                    {garantias?.length === 0 ? (
                                        <Typography variant="h5">No se encontraron resultados</Typography>
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default EstadisticasGarantias;
